import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, LogOut, Printer } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { formatearFechaLarga, localeActual } from '../i18n/fechas';

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA DE FIRMA · PLANA Y FRONTAL (Ann, 16-jul-2026 — reconstruida con ella
// en local, viendo en pantalla lo que veían las clientas)
//
// Ann, textual: "odio los putos frames, ni me los menciones" · "y como tú u otro
// agente use un frame sin ser realmente necesario, lo meto en chirona" · "letra
// pequeña, ventana frontal, se firma y a la calle" · "si quieren leerlo que se
// lo impriman" · "con el puto sistema de scrolls raros; con una página plana
// hubiera sido mejor" · "odio los frames y los scrolls si no son realmente
// imprescindibles".
//
// ANTES: el documento vivía DENTRO de una ventana flotante (<Dialog>) con altura
// máxima y su propio scroll. Una caja dentro de otra caja.
// AHORA: una PÁGINA normal, de arriba abajo, que se baja con el scroll de
// siempre. Sin ventana, sin caja, sin marco. Letra pequeña (es un documento
// legal, no una lectura). Casillas y botón al final. Y quien quiera leerlo con
// calma, que lo IMPRIMA: botón arriba.
//
// El NOMBRE del fichero se mantiene (ConsentimientoGDPRModal) para no romper los
// imports vivos de CanalPilarDashboard.jsx y ClientDashboard.jsx. Ya no es un
// modal: es una página.
// ─────────────────────────────────────────────────────────────────────────────
const ConsentimientoGDPRModal = ({ isOpen, onAccept, onReject, userName }) => {
  const { t } = useTranslation('gestor');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [aceptaNormativa, setAceptaNormativa] = useState(false);
  const [aceptaComunicaciones, setAceptaComunicaciones] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');

  const [normativaHtml, setNormativaHtml] = useState(null);
  const [loadingNormativa, setLoadingNormativa] = useState(false);
  const [falloNormativa, setFalloNormativa] = useState(false);

  // Restos de IMPRESIÓN del documento (Ann 2026-07-13): en pantalla la firma es
  // DIGITAL → se ocultan las líneas "DNI/NIE: ___" y "Lugar, fecha y firma: ___"
  // del texto mostrado (el documento impreso no se toca).
  const _sinLineasImpresion = (html) => {
    try {
      const cont = document.createElement('div');
      cont.innerHTML = html;
      cont.querySelectorAll('p, li, div').forEach(el => {
        const txt = (el.textContent || '').trim();
        if (/^(DNI\/?\s*NIE|Lugar,?\s*fecha y firma)\s*:/i.test(txt)) el.remove();
      });
      return cont.innerHTML;
    } catch (e) { return html; }
  };

  // ── BUCLE INFINITO CERRADO (Guardián 16-jul) ─────────────────────────────
  // ANTES: el efecto llevaba `normativaHtml` como dependencia y al fallar hacía
  // setNormativaHtml(null) → la condición `!normativaHtml` seguía cumpliéndose
  // → volvía a pedirla → fallaba → otra vez… MILES de peticiones por clienta,
  // ahogando el servidor y dejando la pantalla "dando vueltas".
  // AHORA: se pide UNA sola vez (candado con useRef). Si falla, se avisa y se
  // ofrece reintentar A MANO. Un fallo al cargar el TEXTO nunca impide firmar:
  // el consentimiento legal son las CASILLAS marcadas.
  const yaPedida = useRef(false);
  const pedirNormativa = () => {
    setLoadingNormativa(true);
    setFalloNormativa(false);
    axios.get(`${API}/client/normativa`)
      .then(res => {
        const html = res.data?.html ? _sinLineasImpresion(res.data.html) : null;
        setNormativaHtml(html);
        if (!html) setFalloNormativa(true);
      })
      .catch(() => setFalloNormativa(true))
      .finally(() => setLoadingNormativa(false));
  };

  useEffect(() => {
    if (isOpen && !yaPedida.current) {
      yaPedida.current = true;
      pedirNormativa();
    }
  }, [isOpen]); // eslint-disable-line

  const handleAceptar = async () => {
    // Aviso VISIBLE y concreto (Ann 16-jul): le decimos EXACTAMENTE qué le falta
    // y la llevamos a las casillas. Antes era un toast que desaparecía.
    setAviso('');
    const faltan = [];
    if (!aceptaTerminos) faltan.push(t('gdpr.errTerminos'));
    if (!aceptaNormativa) faltan.push(t('gdpr.errNormativa'));
    if (faltan.length) {
      setAviso(faltan.join(' · '));
      try {
        const el = document.getElementById('gdpr-casillas');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) { /* noop */ }
      return;
    }

    setGuardando(true);
    try {
      // Firma DIGITAL (Ann 2026-07-13): sin dibujo. Queda registrado quién acepta
      // y el momento exacto; el backend añade fecha de servidor, IP y user-agent.
      const ahora = new Date();
      await axios.post(`${API}/cliente/aceptar-consentimiento`, {
        firma: `ACEPTACION DIGITAL · ${userName || 'Cliente'} · ${ahora.toISOString()}`,
        acepta_terminos: aceptaTerminos,
        acepta_normativa: aceptaNormativa,
        acepta_comunicaciones: aceptaComunicaciones,
        fecha_aceptacion: ahora.toISOString()
      });
      toast.success(t('gdpr.exito'));
      onAccept();
    } catch (error) {
      // Aviso VISIBLE y quieto, no un toast que se escapa.
      setAviso(t('gdpr.errGuardar'));
      console.error(error);
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  const hoy = formatearFechaLarga(new Date());
  const hora = new Date().toLocaleTimeString(localeActual(), { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="gdpr-pagina">
      <style>{`
        .gdpr-pagina{background:#f2f7f5;min-height:100vh;color:#16323a;
          font-family:Montserrat,-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif}
        .gdpr-centro{max-width:760px;margin:0 auto}
        .gdpr-cab{background:#fff;border-bottom:1px solid #d8e5e0;padding:26px 20px 22px}
        .gdpr-marca{font-size:12px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;
          color:#077069;white-space:nowrap;margin:0 0 16px}
        .gdpr-h1{font-size:25px;font-weight:600;color:#16323a;margin:0 0 8px;line-height:1.25}
        .gdpr-saludo{font-size:15px;color:#2C6675;margin:0}
        .gdpr-cuerpo{padding:26px 20px 44px}
        /* Letra pequeña a propósito: es un documento legal, no una lectura.
           Quien quiera leerlo con calma → botón Imprimir. (Ann, 16-jul) */
        .gdpr-doc{font-size:13px;line-height:1.6;color:#3a4f4d}
        .gdpr-doc h3{font-size:15px;font-weight:600;color:#077069;margin:26px 0 8px}
        .gdpr-doc ul{padding-left:20px;margin:8px 0}
        .gdpr-doc li{margin:5px 0}
        .gdpr-doc p{margin:8px 0}
        .gdpr-doc table{width:100%;border-collapse:collapse;font-size:12px}
        .gdpr-doc td,.gdpr-doc th{border:1px solid #d8e5e0;padding:5px 7px}
        .gdpr-imprimir{display:inline-flex;align-items:center;gap:7px;background:#fff;border:1px solid #9db3ab;
          color:#2C6675;border-radius:8px;padding:9px 15px;font-size:13px;font-weight:600;cursor:pointer;
          margin-bottom:6px}
        .gdpr-imprimir:hover{background:#f2f7f5}
        .gdpr-casilla{display:flex;align-items:flex-start;gap:13px;background:#fff;border:2px solid #9db3ab;
          border-radius:10px;padding:15px;margin:11px 0;cursor:pointer}
        .gdpr-casilla span{font-size:14px;font-weight:500;line-height:1.5}
        .gdpr-casilla.opt{border:1px solid #d8e5e0}
        .gdpr-casilla.opt span{font-weight:400}
        .gdpr-firma{background:#fff;border:2px solid #9db3ab;border-radius:10px;padding:14px 16px;
          margin:22px 0 12px;display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 10px}
        .gdpr-firma b{font-size:15px;font-weight:600}
        .gdpr-firma .et{font-size:12px;font-weight:600;color:#2C6675}
        .gdpr-firma .fe{font-size:14px;color:#2C6675}
        .gdpr-aviso{display:flex;gap:9px;align-items:flex-start;background:#fdeceb;border:2px solid #d9534f;
          border-radius:10px;padding:14px;margin:12px 0}
        .gdpr-aviso p{margin:0;font-size:15px;font-weight:600;color:#8a1f14;line-height:1.45}
        .gdpr-salida{font-size:13px;color:#8a5a20;background:#fdf6ea;border:1px solid #e8cf9e;border-radius:8px;
          padding:14px;line-height:1.55;margin:16px 0 0}
        .gdpr-nota{font-size:12px;color:#6b8280;margin:10px 0 0;line-height:1.5}
        @media print{
          .gdpr-imprimir,.gdpr-casilla,.gdpr-firma,.gdpr-aviso,.gdpr-btns,.gdpr-salida{display:none!important}
          .gdpr-pagina{background:#fff}.gdpr-cab{border:0}.gdpr-doc{font-size:11pt}
        }
      `}</style>

      <header className="gdpr-cab">
        <div className="gdpr-centro">
          <p className="gdpr-marca">Método Pilar Domínguez</p>
          <h1 className="gdpr-h1">{t('gdpr.titulo')}</h1>
          <p className="gdpr-saludo">
            <Trans t={t} i18nKey="gdpr.saludo" values={{ nombre: userName }} components={{ 1: <strong /> }} />
          </p>
        </div>
      </header>

      <div className="gdpr-cuerpo">
        <div className="gdpr-centro">

          {/* Ann: "si quieren leerlo que se lo impriman" */}
          <button type="button" className="gdpr-imprimir" onClick={() => { try { window.print(); } catch (e) { /* noop */ } }}>
            <Printer className="w-4 h-4" />
            {t('gdpr.imprimir')}
          </button>

          <div className="gdpr-doc">
            <h3>{t('gdpr.politicaTitulo')}</h3>
            <p>{t('gdpr.politicaIntro')}</p>
            <ul>
              <li><strong>{t('gdpr.respL')}</strong> {t('gdpr.respT')}</li>
              <li><strong>{t('gdpr.finL')}</strong> {t('gdpr.finT')}</li>
              <li><strong>{t('gdpr.legL')}</strong> {t('gdpr.legT')}</li>
              <li><strong>{t('gdpr.destL')}</strong> {t('gdpr.destT')}</li>
              <li><strong>{t('gdpr.derL')}</strong> {t('gdpr.derT')}</li>
              <li><strong>{t('gdpr.consL')}</strong> {t('gdpr.consT')}</li>
            </ul>

            {loadingNormativa && <p><em>{t('gdpr.cargandoNormativa')}</em></p>}

            {normativaHtml && (
              <>
                <h3>{t('gdpr.normativaTitulo')}</h3>
                <div dangerouslySetInnerHTML={{ __html: normativaHtml }} />
              </>
            )}

            {/* Un fallo al cargar el TEXTO nunca bloquea la firma (Guardián 16-jul):
                el consentimiento legal son las casillas. Antes: reintento infinito. */}
            {falloNormativa && !loadingNormativa && (
              <p className="gdpr-nota">
                {t('gdpr.normativaNoCarga')}{' '}
                <button type="button" className="gdpr-imprimir" style={{ marginTop: 8 }} onClick={pedirNormativa}>
                  {t('gdpr.reintentar')}
                </button>
              </p>
            )}
          </div>

          <div id="gdpr-casillas" style={{ marginTop: 28 }}>
            <label htmlFor="acepta-terminos" className="gdpr-casilla">
              <Checkbox id="acepta-terminos" checked={aceptaTerminos} onCheckedChange={setAceptaTerminos} className="mt-0.5 h-5 w-5" />
              <span>{t('gdpr.aceptoTerminos')}</span>
            </label>
            <label htmlFor="acepta-normativa" className="gdpr-casilla">
              <Checkbox id="acepta-normativa" checked={aceptaNormativa} onCheckedChange={setAceptaNormativa} className="mt-0.5 h-5 w-5" />
              <span>{t('gdpr.aceptoNormativa')}</span>
            </label>
            <label htmlFor="acepta-comunicaciones" className="gdpr-casilla opt">
              <Checkbox id="acepta-comunicaciones" checked={aceptaComunicaciones} onCheckedChange={setAceptaComunicaciones} className="mt-0.5 h-5 w-5" />
              <span>{t('gdpr.aceptoCom')}</span>
            </label>
          </div>

          <div className="gdpr-firma">
            <span className="et">{t('gdpr.firmaDigital')}</span>
            <b>{userName || t('comun.cliente')}</b>
            <span className="fe">{t('gdpr.firmadoEl', { fecha: hoy, hora })}</span>
          </div>

          {aviso && (
            <div className="gdpr-aviso" role="alert">
              <AlertCircle className="w-5 h-5 shrink-0" style={{ color: '#d9534f', marginTop: 1 }} />
              <p>{aviso}</p>
            </div>
          )}

          <div className="gdpr-btns">
            <Button
              onClick={handleAceptar}
              disabled={guardando}
              className="w-full bg-[#077069] hover:bg-[#2C6675] py-6 text-lg"
            >
              {guardando
                ? <>{t('gdpr.guardando')}</>
                : <><CheckCircle2 className="w-5 h-5 mr-2" />{t('gdpr.firmarAceptar')}</>}
            </Button>

            {/* PR-GESTOR-CAMBIOS-F1 · salida limpia si no acepta */}
            {onReject && (
              <>
                <p className="gdpr-salida">{t('gdpr.textoSalida')}</p>
                <Button
                  onClick={onReject}
                  disabled={guardando}
                  variant="outline"
                  className="w-full py-4 text-sm text-[#2C6675] border-[#9db3ab] hover:bg-white mt-3"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('gdpr.noAceptar')}
                </Button>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ConsentimientoGDPRModal;
